import React, { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { useIntl } from 'react-intl'
import { alertError, alertSuccess } from 'utils/notifications'
import API from 'utils/API'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faMapMarkerAlt, faSpinner, faSave } from '@fortawesome/free-solid-svg-icons'
import {
  StyledModal,
  ModalContent,
  HeaderTitle,
  ReadOnlySection,
  AddressField,
  FieldLabel,
  FieldValue,
  EditSection,
  FormGroup,
  FormLabel,
  FormInput,
  FormHint,
  ErrorMessage,
  TwoColumnRow,
  ButtonGroup,
  SecondaryButton,
  PrimaryButton,
  InfoBox,
  LoadingOverlay
} from './EditDeliveryAddressModal.styles'

interface EditDeliveryAddressModalProps {
  open: boolean
  onClose: () => void
  orderId: number
  pharmacyOrderId: number
  type: number // 0 pour para, 1 pour ordo
  currentAddress: {
    address?: string
    address2?: string
    streetNumber?: string
    street?: string
    postalCode?: string
    city?: string
  }
  setData?: (callback: (prevData: any[]) => any[]) => void
  filteredRows?: any[]
  onConfirmAddress?: () => void // Callback pour créer le bon FedEx après validation
  isPendingFedExCreation?: boolean // Si true, on affiche le message de vérification
}

const EditDeliveryAddressModal = (props: EditDeliveryAddressModalProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const [formData, setFormData] = useState({
    address: props.currentAddress.address || '',
    streetNumber: props.currentAddress.streetNumber || '',
    street: props.currentAddress.street || '',
    postalCode: props.currentAddress.postalCode || '',
    city: props.currentAddress.city || '',
    address2: props.currentAddress.address2 || ''
  })

  // Mettre à jour le formulaire quand l'adresse change
  useEffect(() => {
    console.log('📍 EditDeliveryAddressModal - currentAddress reçu:', props.currentAddress)
    console.log('📍 Modal ouverte:', props.open)
    setFormData({
      address: props.currentAddress.address || '',
      streetNumber: props.currentAddress.streetNumber || '',
      street: props.currentAddress.street || '',
      postalCode: props.currentAddress.postalCode || '',
      city: props.currentAddress.city || '',
      address2: props.currentAddress.address2 || ''
    })
  }, [props.currentAddress, props.open])

  // Reset du mode édition à l'ouverture
  useEffect(() => {
    if (props.open) {
      setIsEditing(false)
      setErrors({})
    }
  }, [props.open])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.streetNumber.trim()) {
      newErrors.streetNumber = 'Le numéro de rue est requis'
    }

    if (!formData.street.trim()) {
      newErrors.street = 'Le nom de la rue est requis'
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Le code postal est requis'
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'Le code postal doit contenir 5 chiffres'
    }

    if (!formData.city.trim()) {
      newErrors.city = 'La ville est requise'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      alertError('Veuillez corriger les erreurs dans le formulaire')
      return
    }

    try {
      setIsLoading(true)

      // Préparer les données pour l'API
      const deliveryAddressData = {
        address: formData.address,
        rawAddress: formData.address,
        street_number: formData.streetNumber,
        street: formData.street,
        postalCode: formData.postalCode,
        city: formData.city,
        address2: formData.address2
      }

      // Déterminer la collection (para ou ordo)
      const collection = props.type === 0 ? 'para' : 'ordo'

      // Envoyer la requête
      const response = await API.post('/order/update-delivery-address', {
        orderId: props.orderId,
        collection: collection,
        deliveryAddress: deliveryAddressData
      })

      if (response.data.success) {
        alertSuccess('Adresse de livraison mise à jour avec succès')

        // Mise à jour locale de la commande sans tout recharger (comme pour FedEx)
        if (props.setData && props.filteredRows) {
          props.setData((prevData: any[]) =>
            prevData.map((order: any) =>
              (order.orderId === props.orderId && order.pharmacyOrderId === props.pharmacyOrderId)
                ? {
                    ...order,
                    deliveryAddress: formData.address,
                    deliveryAddress2: formData.address2,
                    deliveryAddressStreetNumber: formData.streetNumber,
                    deliveryAddressStreet: formData.street,
                    deliveryAddressPostalCode: formData.postalCode,
                    deliveryAddressCity: formData.city
                  }
                : order
            )
          )
        }

        // Si on est en attente de création FedEx, on la déclenche
        if (props.isPendingFedExCreation && props.onConfirmAddress) {
          props.onConfirmAddress()
        }

        props.onClose()
      } else {
        alertError("Erreur lors de la mise à jour de l'adresse")
      }
    } catch (error: any) {
      console.error('Error updating delivery address:', error)
      alertError(
        error?.response?.data?.message || "Erreur lors de la mise à jour de l'adresse"
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    if (isEditing) {
      // Retour en mode lecture
      setIsEditing(false)
      setErrors({})
      // Réinitialiser le formulaire
      setFormData({
        address: props.currentAddress.address || '',
        streetNumber: props.currentAddress.streetNumber || '',
        street: props.currentAddress.street || '',
        postalCode: props.currentAddress.postalCode || '',
        city: props.currentAddress.city || '',
        address2: props.currentAddress.address2 || ''
      })
    } else {
      // Fermer la modal
      props.onClose()
    }
  }

  const handleConfirmAndCreate = () => {
    // Confirmer l'adresse et créer le bon FedEx
    if (props.onConfirmAddress) {
      props.onConfirmAddress()
    }
    props.onClose()
  }

  const toggle = () => {
    if (!isLoading) {
      handleCancel()
    }
  }

  return (
    <StyledModal isOpen={props.open} toggle={toggle} backdrop="static" size="lg">
      {isLoading && (
        <LoadingOverlay>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Mise à jour de l'adresse de livraison...</p>
        </LoadingOverlay>
      )}

      <ModalHeader toggle={toggle}>
        <HeaderTitle>
          <FontAwesomeIcon icon={isEditing ? faEdit : faMapMarkerAlt} />
          {isEditing ? 'Modifier l\'adresse de livraison' : 'Adresse de livraison'}
        </HeaderTitle>
      </ModalHeader>

      <ModalBody>
        <ModalContent>
          {!isEditing ? (
            // MODE LECTURE
            <ReadOnlySection>
              <InfoBox>
                📦 <strong>Adresse sur le colis :</strong> {formData.streetNumber} {formData.street}, {formData.postalCode} {formData.city}
              </InfoBox>

              <AddressField>
                <FieldLabel>Adresse complète (indicative)</FieldLabel>
                <FieldValue>{formData.address || 'Non renseignée'}</FieldValue>
              </AddressField>

              <TwoColumnRow>
                <AddressField>
                  <FieldLabel>Numéro de rue</FieldLabel>
                  <FieldValue>{formData.streetNumber || 'Non renseigné'}</FieldValue>
                </AddressField>

                <AddressField>
                  <FieldLabel>Nom de la rue</FieldLabel>
                  <FieldValue>{formData.street || 'Non renseigné'}</FieldValue>
                </AddressField>
              </TwoColumnRow>

              <TwoColumnRow>
                <AddressField>
                  <FieldLabel>Code postal</FieldLabel>
                  <FieldValue>{formData.postalCode || 'Non renseigné'}</FieldValue>
                </AddressField>

                <AddressField>
                  <FieldLabel>Ville</FieldLabel>
                  <FieldValue>{formData.city || 'Non renseigné'}</FieldValue>
                </AddressField>
              </TwoColumnRow>

              <AddressField>
                <FieldLabel>Complément d'adresse</FieldLabel>
                <FieldValue>{formData.address2 || 'Non renseigné'}</FieldValue>
              </AddressField>
            </ReadOnlySection>
          ) : (
            // MODE ÉDITION
            <EditSection>
              <InfoBox>
                📦 <strong>Adresse sur le colis :</strong> {formData.streetNumber} {formData.street}, {formData.postalCode} {formData.city}
              </InfoBox>

              <AddressField>
                <FieldLabel>Adresse complète (indicative, non modifiable)</FieldLabel>
                <FieldValue style={{ background: '#f5f5f5', opacity: 0.8 }}>
                  {formData.address || 'Non renseignée'}
                </FieldValue>
              </AddressField>

              <TwoColumnRow>
                <FormGroup hasError={!!errors.streetNumber}>
                  <FormLabel>Numéro de rue *</FormLabel>
                  <FormInput
                    type="text"
                    value={formData.streetNumber}
                    onChange={(e) => handleInputChange('streetNumber', e.target.value)}
                    placeholder="2d"
                    hasError={!!errors.streetNumber}
                    disabled={isLoading}
                  />
                  {errors.streetNumber && <ErrorMessage>{errors.streetNumber}</ErrorMessage>}
                </FormGroup>

                <FormGroup hasError={!!errors.street}>
                  <FormLabel>Nom de la rue *</FormLabel>
                  <FormInput
                    type="text"
                    value={formData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    placeholder="Rue Ho Chi Minh"
                    hasError={!!errors.street}
                    disabled={isLoading}
                  />
                  {errors.street && <ErrorMessage>{errors.street}</ErrorMessage>}
                </FormGroup>
              </TwoColumnRow>

              <TwoColumnRow>
                <FormGroup hasError={!!errors.postalCode}>
                  <FormLabel>Code postal *</FormLabel>
                  <FormInput
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    placeholder="69120"
                    maxLength={5}
                    hasError={!!errors.postalCode}
                    disabled={isLoading}
                  />
                  {errors.postalCode && <ErrorMessage>{errors.postalCode}</ErrorMessage>}
                </FormGroup>

                <FormGroup hasError={!!errors.city}>
                  <FormLabel>Ville *</FormLabel>
                  <FormInput
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="Vaulx-en-Velin"
                    hasError={!!errors.city}
                    disabled={isLoading}
                  />
                  {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                </FormGroup>
              </TwoColumnRow>

              <FormGroup>
                <FormLabel>Complément d'adresse (optionnel)</FormLabel>
                <FormInput
                  type="text"
                  value={formData.address2}
                  onChange={(e) => handleInputChange('address2', e.target.value)}
                  placeholder="Bâtiment, étage, code d'accès..."
                  disabled={isLoading}
                />
                <FormHint>Bâtiment, étage, appartement, code d'accès</FormHint>
              </FormGroup>
            </EditSection>
          )}
        </ModalContent>
      </ModalBody>

      <ModalFooter>
        <ButtonGroup>
          {!isEditing ? (
            <>
              <SecondaryButton onClick={handleCancel} disabled={isLoading}>
                {props.isPendingFedExCreation ? 'Annuler' : 'Fermer'}
              </SecondaryButton>

              {props.isPendingFedExCreation ? (
                <PrimaryButton onClick={handleConfirmAndCreate} disabled={isLoading}>
                  ✅ Confirmer et créer le bon
                </PrimaryButton>
              ) : null}

              <PrimaryButton onClick={() => setIsEditing(true)} disabled={isLoading}>
                <FontAwesomeIcon icon={faEdit} />
                <span style={{ marginLeft: '8px' }}>Modifier</span>
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton onClick={handleCancel} disabled={isLoading}>
                Annuler
              </SecondaryButton>
              <PrimaryButton onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span style={{ marginLeft: '8px' }}>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    <span style={{ marginLeft: '8px' }}>Enregistrer</span>
                  </>
                )}
              </PrimaryButton>
            </>
          )}
        </ButtonGroup>
      </ModalFooter>
    </StyledModal>
  )
}

export default EditDeliveryAddressModal
